import type { MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { ProgressivePicture } from '~/components/progressive-picture'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { ArrowRight, Camera, Euro, Clock, MapPin, Sparkles } from 'lucide-react'
import { VoirAussi, CROSS_LINKS } from '~/components/voir-aussi'

export function headers() { return STATIC_PAGE_HEADERS }

export async function loader() {
  const { AVANT_APRES_PROJECTS, AVANT_APRES_CATEGORIES } = await import('~/lib/avant-apres-data.server')
  // Slim list — cards render slug/title/category label/before+after images/
  // shortDesc/budget/duration/location. The long `description` and `keyWorks`
  // bodies stay server-side.
  const projects = AVANT_APRES_PROJECTS.map((p) => ({
    slug: p.slug,
    title: p.title,
    shortDesc: p.shortDesc,
    categoryLabel: AVANT_APRES_CATEGORIES[p.category],
    beforeImage: p.beforeImage,
    afterImage: p.afterImage,
    budget: p.budget,
    duration: p.duration,
    location: p.location,
  }))
  return { projects }
}

export const meta: MetaFunction = () => [
  { title: "Projets avant/après — Inspirations rénovation | AfficheServices" },
  { name: 'description', content: "Galerie de projets de rénovation avant/après : salle de bain, cuisine, façade, toiture. Budgets réalistes, durées, conseils d'artisans." },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/avant-apres' },
  { property: 'og:title', content: 'Projets avant/après — Inspirations rénovation | AfficheServices' },
  { property: 'og:description', content: "Galerie de projets de rénovation avant/après : salle de bain, cuisine, façade, toiture." },
  { property: 'og:url', content: 'https://www.afficheservices.fr/avant-apres' },
]

export default function AvantApresIndexPage() {
  const { projects } = useLoaderData<typeof loader>()
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Avant / Après', url: 'https://www.afficheservices.fr/avant-apres' },
      ]} />

      <section className="bg-gradient-to-br from-rose-900 via-fuchsia-900 to-slate-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-rose-200">
            <Camera className="h-3.5 w-3.5" /> Galerie d'inspiration
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">Projets Avant / Après</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-rose-200">
            {projects.length} exemples concrets de rénovation avec budgets réalistes,
            durées de chantier et conseils d'artisans.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 lg:px-6">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <Link
              key={p.slug}
              to={`/avant-apres/${p.slug}`}
              className="group overflow-hidden rounded-2xl border border-border bg-white shadow-xs transition-all hover:shadow-md"
            >
              {/* Split before/after thumbnail (AVIF + WebP + LQIP) */}
              <div className="relative grid grid-cols-2 bg-slate-100">
                <div className="relative">
                  <ProgressivePicture
                    baseUrl={p.beforeImage.split('?')[0]}
                    alt={`Avant : ${p.title}`}
                    width={400}
                    height={320}
                    className="h-40 w-full"
                  />
                  <span className="absolute left-1 top-1 rounded bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                    Avant
                  </span>
                </div>
                <div className="relative">
                  <ProgressivePicture
                    baseUrl={p.afterImage.split('?')[0]}
                    alt={`Après : ${p.title}`}
                    width={400}
                    height={320}
                    className="h-40 w-full"
                  />
                  <span className="absolute left-1 top-1 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                    Après
                  </span>
                </div>
              </div>

              <div className="p-5">
                <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-0.5 text-[11px] font-semibold text-rose-700">
                  {p.categoryLabel}
                </span>
                <h2 className="mt-2 text-sm font-bold text-slate-900 group-hover:text-primary line-clamp-2">
                  {p.title}
                </h2>
                <p className="mt-1 text-xs text-slate-500 line-clamp-2">{p.shortDesc}</p>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                  <span className="flex items-center gap-1">
                    <Euro className="h-3 w-3" /> {p.budget}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {p.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {p.location}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <section className="mt-16 rounded-2xl border border-border bg-slate-50 p-6 lg:p-10">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold text-slate-900">Votre projet en tête ?</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">
            Recevez 3 devis gratuits d'artisans vérifiés près de chez vous et transformez votre projet en
            réalité. Budget maîtrisé, délais respectés, garantie décennale.
          </p>
          <Link
            to="/devis-gratuit"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-white hover:bg-primary-hover"
          >
            Demander mes devis <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        <VoirAussi links={CROSS_LINKS.inspiration} />

        <p className="mt-8 text-center text-[11px] text-slate-400">
          Les photographies présentées sont des exemples d'inspiration et ne proviennent pas de clients
          AfficheServices. Les budgets et durées sont des moyennes du marché pour chaque type de chantier.
        </p>
      </div>

      <Footer />
    </div>
  )
}
